import { useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import StepNavigation from "./StepNavigation";
import CameraCapture, { type CameraCaptureHandle } from "./CameraCapture";
import type { SelfieCapture } from "./types";

export interface SelfieCaptureStepProps {
  defaultValue: SelfieCapture | null;
  isFirstStep: boolean;
  isLastStep: boolean;
  onSubmit: (capture: SelfieCapture) => void;
  onBack: () => void;
}

const SelfieCaptureStep = ({
  defaultValue,
  isFirstStep,
  isLastStep,
  onSubmit,
  onBack,
}: SelfieCaptureStepProps): JSX.Element => {
  const cameraRef = useRef<CameraCaptureHandle | null>(null);
  const [capture, setCapture] = useState<SelfieCapture | null>(defaultValue);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const handleCapture = useCallback(() => {
    const screenshot = cameraRef.current?.getScreenshot();
    if (!screenshot) {
      setCameraError("Could not capture image. Please try again.");
      return;
    }
    setCapture({ imageDataUrl: screenshot, capturedAt: new Date().toISOString() });
  }, []);

  const handleRetake = useCallback(() => setCapture(null), []);

  const handleNext = useCallback(() => {
    if (capture) onSubmit(capture);
  }, [capture, onSubmit]);

  return (
    <Box
      sx={{
        bgcolor: "common.white",
        borderRadius: "20px",
        pt: "24px",
        px: "24px",
        pb: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {cameraError && (
        <Alert
          severity="error"
          sx={{ mb: 2, width: "100%", borderRadius: "10px" }}
          onClose={() => setCameraError(null)}
        >
          {cameraError}
        </Alert>
      )}

      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "19.2px",
          color: "common.black",
          pb: "24px",
          width: "100%",
        }}
      >
        Position your face inside the frame in a well-lit area, then tap capture.
      </Typography>

      {/* Camera viewport */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          aspectRatio: "560 / 426",
          borderRadius: "20px",
          overflow: "hidden",
          bgcolor: capture ? "#212121" : "#666",
          boxShadow:
            "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
        }}
      >
        {capture ? (
          /* ── Post-capture: show captured image + Retake bar ── */
          <>
            <Box
              component="img"
              src={capture.imageDataUrl}
              alt="Captured selfie"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              onClick={handleRetake}
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "44px",
                bgcolor: "#fee9e7",
                opacity: 0.7,
                borderBottomLeftRadius: "20px",
                borderBottomRightRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              <RestartAltRoundedIcon sx={{ fontSize: "22px", color: "common.black" }} />
              <Typography
                sx={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 700,
                  fontSize: "12px",
                  lineHeight: "16.8px",
                  letterSpacing: "0.084px",
                  textTransform: "uppercase",
                  color: "common.black",
                }}
              >
                Retake
              </Typography>
            </Box>
          </>
        ) : (
          /* ── Pre-capture: live camera + oval guide + Capture bar ── */
          <>
            <CameraCapture
              ref={cameraRef}
              facingMode="user"
              mirrored
              onError={setCameraError}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Oval face guide — percentage-based so it scales with the viewport on all screen sizes */}
            <Box
              sx={{
                position: "absolute",
                top: "3.3%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "40%",
                height: "70%",
                borderRadius: "50%",
                border: "3px dashed rgba(255,255,255,0.9)",
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
            {/* Capture selfie bar pinned to bottom of viewport */}
            <Box
              onClick={!cameraError ? handleCapture : undefined}
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "44px",
                bgcolor: "common.black",
                borderBottomLeftRadius: "20px",
                borderBottomRightRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: cameraError ? "not-allowed" : "pointer",
                zIndex: 2,
              }}
            >
              <CameraAltRoundedIcon sx={{ fontSize: "22px", color: "common.white" }} />
              <Typography
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  lineHeight: "28px",
                  color: "common.white",
                  textTransform: "capitalize",
                }}
              >
                Capture selfie
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <StepNavigation
        onBack={onBack}
        onNext={handleNext}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        nextDisabled={!capture}
        sx={{ padding: "16px 0 0 0" }}
      />
    </Box>
  );
};

export default SelfieCaptureStep;
